import { Visibility } from '@mui/icons-material';
import { Box, Button, CircularProgress, Dialog, DialogContent, Divider, Grid, Stack, Switch, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useGetShopConfig, useUpdateShopConfig } from '../../../services/core/settings/settings.hooks';
import { useGetShopDetails } from '../../../services/core/shop';
import EmailPreviewModal from './EmailPreviewModal';

const ApexAutoAppEmailSettings = ({ configData: propConfigData }) => {
    const { data: queryConfigData, isLoading: isConfigLoading } = useGetShopConfig();
    const { data: shopDetails } = useGetShopDetails();
    const updateShopConfigMutation = useUpdateShopConfig();

    const configData = propConfigData || queryConfigData;

    const [welcomeEmailChecked, setWelcomeEmailChecked] = useState(true);
    const [bookingSectionChecked, setBookingSectionChecked] = useState(true);

    const [previewModalState, setPreviewModalState] = useState({
        open: false,
        type: null,
    });

    // Hydrate state from shop config API data
    useEffect(() => {
        const config = configData?.config || configData;
        if (config) {
            if (config.defaultSendWelcomeEmailToCustomer !== undefined) {
                setWelcomeEmailChecked(Boolean(config.defaultSendWelcomeEmailToCustomer));
            }
            if (config.showAppPromotionInEmails !== undefined) {
                setBookingSectionChecked(Boolean(config.showAppPromotionInEmails));
            }
        }
    }, [configData]);

    const handleCancel = () => {
        const config = configData?.config || configData;
        if (config) {
            if (config.defaultSendWelcomeEmailToCustomer !== undefined) {
                setWelcomeEmailChecked(Boolean(config.defaultSendWelcomeEmailToCustomer));
            }
            if (config.showAppPromotionInEmails !== undefined) {
                setBookingSectionChecked(Boolean(config.showAppPromotionInEmails));
            }
        }
    };

    const handleSave = async () => {
        try {
            const payload = {
                defaultSendWelcomeEmailToCustomer: welcomeEmailChecked,
                showAppPromotionInEmails: bookingSectionChecked,
            };
            const response = await updateShopConfigMutation.mutateAsync(payload);
            toast.success(response?.message || 'Changes Saved');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to update shop configuration');
        }
    };

    const handlePreviewWelcomeEmail = () => {
        setPreviewModalState({
            open: true,
            type: 'welcome_email',
        });
    };

    const handlePreviewBookingSection = () => {
        setPreviewModalState({
            open: true,
            type: 'booking_section',
        });
    };

    const handleClosePreviewModal = () => {
        setPreviewModalState({
            open: false,
            type: null,
        });
    };

    return (
        <Box>
            <Grid
                container
                sx={{
                    boxShadow: 3,
                    bgcolor: 'white',
                    padding: '24px',
                    borderRadius: '10px',
                    marginTop: '32px',
                    justifyContent: 'space-between',
                }}
            >
                {/* Left Column: Title and Description */}
                <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
                    <Typography variant="h5" sx={{ fontWeight: 400, fontSize: '24px', color: '#1A1A1A' }}>
                        Apex Auto App Branding
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            mt: '12px',
                            fontSize: '16px',
                            color: '#000000',
                            lineHeight: '24px',
                            letterSpacing: '0.15px',
                            maxWidth: 360,
                        }}
                    >
                        Choose whether customers receive an Apex Auto App welcome email and see an Apex Auto App booking section in your emails. Your shop name
                        is always shown.
                    </Typography>
                </Grid>

                {/* Right Column: Settings and Action Items */}
                <Grid item xs={12} sm={8} md={8} lg={8} xl={8}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {/* Item 1: Welcome to “Apex Auto App” Email */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
                            {/* Part 1: Heading & Subheading */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontSize: '20px',
                                        fontWeight: 500,
                                        lineHeight: '160%',
                                        letterSpacing: '0.15px',
                                    }}
                                >
                                    “Welcome to Apex Auto App” Email
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                        fontStyle: 'normal',
                                        fontWeight: 400,
                                        lineHeight: '150%',
                                        letterSpacing: '0.15px',
                                        mt: '4px',
                                    }}
                                >
                                    Sends customers a &quot;Welcome to Apex Auto&quot; email when they are added a customer to your shop&apos;s customer list.
                                </Typography>
                            </Box>

                            {/* Part 2: Preview button */}
                            <Box
                                onClick={handlePreviewWelcomeEmail}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    color: '#006CDD',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                    '&:hover': {
                                        opacity: 0.85,
                                    },
                                }}
                            >
                                <Visibility sx={{ fontSize: 24, color: '#006CDD' }} />
                                <Typography
                                    component="span"
                                    sx={{
                                        color: '#006CDD',
                                        fontSize: '15px',
                                        fontWeight: 500,
                                        lineHeight: '26px',
                                        letterSpacing: '0.46px',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    PREVIEW
                                </Typography>
                            </Box>

                            {/* Part 3: Toggle button */}
                            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                                <Switch checked={welcomeEmailChecked} onChange={(e) => setWelcomeEmailChecked(e.target.checked)} color="primary" />
                            </Box>
                        </Box>

                        {/* Divider with 24px space top and bottom */}
                        <Divider sx={{ my: '24px', borderColor: 'rgba(0, 0, 0, 0.10)' }} />

                        {/* Item 2: App Download Branding in emails */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
                            {/* Part 1: Heading & Subheading */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontSize: '20px',
                                        fontWeight: 500,
                                        lineHeight: '160%',
                                        letterSpacing: '0.15px',
                                    }}
                                >
                                    “Book with Apex Auto App” Email Section
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                        fontWeight: 400,
                                        lineHeight: '150%',
                                        letterSpacing: '0.15px',
                                        mt: '4px',
                                    }}
                                >
                                    Add a &quot;Book with the Apex Auto App&quot; section to invoice, estimate, and appointment emails.
                                </Typography>
                            </Box>

                            {/* Part 2: Preview button */}
                            <Box
                                onClick={handlePreviewBookingSection}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    color: '#006CDD',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                    '&:hover': {
                                        opacity: 0.85,
                                    },
                                }}
                            >
                                <Visibility sx={{ fontSize: 24, color: '#006CDD' }} />
                                <Typography
                                    component="span"
                                    sx={{
                                        color: '#006CDD',
                                        fontSize: '15px',
                                        fontWeight: 500,
                                        lineHeight: '26px',
                                        letterSpacing: '0.46px',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    PREVIEW
                                </Typography>
                            </Box>

                            {/* Part 3: Toggle button */}
                            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                                <Switch checked={bookingSectionChecked} onChange={(e) => setBookingSectionChecked(e.target.checked)} color="primary" />
                            </Box>
                        </Box>

                        {/* Bottom Right Actions with 24px top margin */}
                        <Grid container justifyContent="flex-end" spacing={2} sx={{ marginTop: '24px' }}>
                            <Grid item>
                                <Stack spacing={2} direction="row">
                                    <Button variant="outlined" color="primary" onClick={handleCancel}>
                                        CANCEL
                                    </Button>
                                    <Button variant="contained" color="primary" onClick={handleSave} disabled={updateShopConfigMutation.isPending}>
                                        SAVE
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
            </Grid>

            {/* Email Preview Modal Component */}
            <EmailPreviewModal
                open={previewModalState.open}
                onClose={handleClosePreviewModal}
                type={previewModalState.type}
                shopDetails={shopDetails}
                bookingSectionChecked={bookingSectionChecked}
            />

            {/* Dialog Loader during API fetching or update mutation */}
            <Dialog fullScreen={false} open={isConfigLoading || updateShopConfigMutation.isPending}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default ApexAutoAppEmailSettings;
